import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { projectAPI, angeboteAPI, profileAPI, reviewAPI } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MapPin,
  TreeDeciduous,
  Calendar,
  Ruler,
  ArrowLeft,
  Clock,
  Star,
  User,
  Send,
  CheckCircle2,
  MessageSquare,
  Award,
  Building2,
  Phone
} from 'lucide-react';
import { PriceEstimation } from '@/components/PriceEstimation';
import { GermanyMap } from '@/components/GermanyMap';
import { CO2Calculation } from '@/components/CO2Calculation';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [angebote, setAngebote] = useState([]);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [ownerReviews, setOwnerReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [offerForm, setOfferForm] = useState({
    preis: '',
    beschreibung: '',
    zeitrahmen: '',
    verfuegbar_ab: ''
  });

  const isWaldbesitzer = user?.role === 'waldbesitzer';
  const isOwner = project?.owner_id === user?.id;
  const hasSubmittedOffer = angebote.some(a => a.anbieter_id === user?.id);

  useEffect(() => {
    fetchProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, angeboteRes] = await Promise.all([
        projectAPI.get(id),
        angeboteAPI.listForProject(id)
      ]);
      
      setProject(projectRes.data);
      setAngebote(angeboteRes.data);

      // Fetch owner profile and reviews
      if (projectRes.data.owner_id) {
        const [profileRes, reviewsRes] = await Promise.all([
          profileAPI.getProfile(projectRes.data.owner_id),
          reviewAPI.getForUser(projectRes.data.owner_id)
        ]);
        setOwnerProfile(profileRes.data);
        setOwnerReviews(reviewsRes.data);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error(t('toasts.projektNichtGefunden'));
      navigate('/projekte');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOffer = async () => {
    if (!offerForm.preis || !offerForm.beschreibung) {
      toast.error(t('toasts.preisUndBeschreibung'));
      return;
    }

    setSubmitting(true);
    try {
      await angeboteAPI.submit(id, {
        preis: parseFloat(offerForm.preis),
        beschreibung: offerForm.beschreibung,
        zeitrahmen: offerForm.zeitrahmen || null,
        verfuegbar_ab: offerForm.verfuegbar_ab || null
      });
      
      toast.success(t('toasts.angebotErfolg'));
      setShowOfferDialog(false);
      fetchProjectData();
    } catch (error) {
      console.error('Error submitting offer:', error);
      toast.error(error.response?.data?.detail || t('toasts.angebotFehler'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptOffer = async (angebotId) => {
    try {
      await angeboteAPI.accept(angebotId);
      toast.success(t('toasts.angebotAngenommen'));
      fetchProjectData();
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error(t('toasts.angebotAnnehmFehler'));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      offen: { label: 'Offen', className: 'bg-blue-100 text-blue-700 border-blue-200' },
      angebote_erhalten: { label: 'Angebote erhalten', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      vergeben: { label: 'Vergeben', className: 'bg-purple-100 text-purple-700 border-purple-200' },
      in_arbeit: { label: 'In Arbeit', className: 'bg-orange-100 text-orange-700 border-orange-200' },
      abgeschlossen: { label: 'Abgeschlossen', className: 'bg-green-100 text-green-700 border-green-200' },
    };
    const c = config[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={`${c.className} border`}>{c.label}</Badge>;
  };

  const renderStars = (rating, size = 'sm') => {
    const sizeClass = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${
              star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-primary">Laden...</div>
        </div>
      </AppShell>
    );
  }

  if (!project) return null;

  return (
    <AppShell>
      <div className="space-y-6" data-testid="project-detail-page">
        {/* Back Link */}
        <Link 
          to="/projekte" 
          className="inline-flex items-center gap-2 text-gray-500 hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zu Projekten
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* A. Project Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                        {project.kategorie_name}
                      </Badge>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {project.bundesland}
                      </span>
                    </div>
                    <h1 className="font-serif text-2xl md:text-3xl font-bold text-gray-900">
                      {project.titel}
                    </h1>
                  </div>
                  {getStatusBadge(project.status)}
                </div>

                {/* B. Overview Card */}
                <div className="grid sm:grid-cols-4 gap-4 p-4 bg-warm-100 rounded-xl">
                  <div className="text-center">
                    <Ruler className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-lg font-bold text-gray-900">{project.flaeche_ha} ha</p>
                    <p className="text-xs text-gray-500">Fläche</p>
                  </div>
                  <div className="text-center">
                    <TreeDeciduous className="h-5 w-5 text-secondary mx-auto mb-1" />
                    <p className="text-lg font-bold text-gray-900">
                      {project.baumarten?.length > 0 ? project.baumarten[0] : '–'}
                    </p>
                    <p className="text-xs text-gray-500">Baumart</p>
                  </div>
                  <div className="text-center">
                    <Award className="h-5 w-5 text-accent mx-auto mb-1" />
                    <p className="text-lg font-bold text-gray-900">{project.kategorie_name}</p>
                    <p className="text-xs text-gray-500">Maßnahme</p>
                  </div>
                  <div className="text-center">
                    <Calendar className="h-5 w-5 text-gray-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-gray-900">
                      {project.zeitraum_start 
                        ? new Date(project.zeitraum_start).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })
                        : 'Flexibel'}
                    </p>
                    <p className="text-xs text-gray-500">Zeitraum</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* C. Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Projektbeschreibung</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">
                  {project.beschreibung}
                </p>

                {project.baumarten?.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-warm-200">
                    <p className="text-sm text-gray-500 mb-2">Baumarten:</p>
                    <div className="flex flex-wrap gap-2">
                      {project.baumarten.map((baumart) => (
                        <Badge key={baumart} variant="secondary" className="bg-secondary/10 text-secondary">
                          {baumart}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {project.ort && (
                  <div className="mt-4 pt-4 border-t border-warm-200">
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {project.ort}, {project.bundesland}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* F. Offers Comparison (for Waldbesitzer) */}
            {isOwner && angebote.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Eingegangene Angebote
                    <Badge variant="secondary">{angebote.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {angebote.map((angebot) => (
                      <div
                        key={angebot.id}
                        className={`p-4 rounded-xl border ${
                          angebot.status === 'angenommen'
                            ? 'border-green-200 bg-green-50'
                            : angebot.status === 'abgelehnt'
                            ? 'border-gray-200 bg-gray-50 opacity-60'
                            : 'border-warm-200 hover:border-primary/30'
                        } transition-colors`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                                <User className="h-5 w-5 text-accent" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {angebot.anbieter_company || angebot.anbieter_name}
                                </p>
                                <div className="flex items-center gap-2 text-sm">
                                  {renderStars(angebot.anbieter_rating)}
                                  <span className="text-gray-500">
                                    ({angebot.anbieter_review_count} Bewertungen)
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{angebot.beschreibung}</p>
                            {angebot.zeitrahmen && (
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {angebot.zeitrahmen}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">
                              {angebot.preis.toLocaleString('de-DE')} €
                            </p>
                            {angebot.status === 'eingereicht' && project.status !== 'vergeben' && (
                              <div className="mt-3 space-y-2">
                                <Button
                                  onClick={() => handleAcceptOffer(angebot.id)}
                                  className="w-full bg-primary hover:bg-primary-600"
                                  size="sm"
                                >
                                  <CheckCircle2 className="mr-2 h-4 w-4" />
                                  Annehmen
                                </Button>
                                <Link to={`/anbieter/${angebot.anbieter_id}`}>
                                  <Button variant="outline" size="sm" className="w-full">
                                    Profil ansehen
                                  </Button>
                                </Link>
                              </div>
                            )}
                            {angebot.status === 'angenommen' && (
                              <Badge className="mt-2 bg-green-100 text-green-700">
                                Angenommen
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* G. Reviews Section */}
            {ownerReviews.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Bewertungen des Waldbesitzers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {ownerReviews.slice(0, 3).map((review) => (
                      <div key={review.id} className="p-4 bg-warm-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          {renderStars(review.rating)}
                          <span className="text-sm text-gray-500">
                            von {review.reviewer_name}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm">{review.kommentar}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* D. Trust Section - Owner Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Waldbesitzer</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{project.owner_name}</p>
                    {ownerProfile?.bundesland && (
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {ownerProfile.bundesland}
                      </p>
                    )}
                  </div>
                </div>
                
                {ownerProfile?.avg_rating > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    {renderStars(ownerProfile.avg_rating, 'md')}
                    <span className="text-sm text-gray-500">
                      ({ownerProfile.review_count} Bewertungen)
                    </span>
                  </div>
                )}

                {ownerProfile?.description && (
                  <p className="text-sm text-gray-600 mb-4">{ownerProfile.description}</p>
                )}

                <div className="text-xs text-gray-400">
                  Projekt erstellt am {new Date(project.created_at).toLocaleDateString('de-DE')}
                </div>
              </CardContent>
            </Card>

            {/* E. Offer Section (for Anbieter) */}
            {!isWaldbesitzer && !isOwner && (
              <Card className="border-accent/30">
                <CardHeader className="bg-accent/5">
                  <CardTitle className="text-lg">Angebot abgeben</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {hasSubmittedOffer ? (
                    <div className="text-center py-4">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="font-medium text-gray-900">Angebot abgegeben</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Sie haben bereits ein Angebot für dieses Projekt eingereicht
                      </p>
                    </div>
                  ) : project.status !== 'offen' && project.status !== 'angebote_erhalten' ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">
                        Dieses Projekt akzeptiert keine neuen Angebote
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-4">
                        Interessiert an diesem Projekt? Geben Sie ein Angebot ab und beschreiben Sie, 
                        warum Sie der richtige Partner sind.
                      </p>
                      <Button
                        onClick={() => setShowOfferDialog(true)}
                        className="w-full bg-accent hover:bg-accent/90"
                        data-testid="submit-offer-btn"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Angebot abgeben
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* H. Call to Action for Owner */}
            {isOwner && project.status === 'offen' && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-6 text-center">
                  <Clock className="h-10 w-10 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Warten auf Angebote
                  </h3>
                  <p className="text-sm text-gray-600">
                    Ihr Projekt ist sichtbar für alle Anbieter. 
                    Sie werden benachrichtigt, sobald Angebote eingehen.
                  </p>
                </CardContent>
              </Card>
            )}

            {isOwner && angebote.length > 0 && project.status === 'angebote_erhalten' && (
              <Card className="border-amber-300 bg-amber-50">
                <CardContent className="p-6 text-center">
                  <Star className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {angebote.length} Angebote verfügbar
                  </h3>
                  <p className="text-sm text-gray-600">
                    Vergleichen Sie die Angebote und wählen Sie den passenden Anbieter
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Contact Info */}
            {!isOwner && ownerProfile?.phone && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500 mb-2">Kontakt</p>
                  <a 
                    href={`tel:${ownerProfile.phone}`}
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {ownerProfile.phone}
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Price Estimation */}
            <PriceEstimation
              flaeche_ha={project.flaeche_ha}
              kategorie={project.kategorie}
              bundesland={project.bundesland}
              baumart={project.baumarten?.[0] || null}
              compact={false}
            />

            {/* CO2 Calculation - Show for CO2 projects or aufforstung */}
            {(project.kategorie === 'co2_projekt' || project.kategorie === 'aufforstung') && (
              <CO2Calculation
                flaeche_ha={project.flaeche_ha}
                baumart={project.baumarten?.[0] || null}
                years={10}
                compact={false}
              />
            )}

            {/* Map */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Standort</CardTitle>
              </CardHeader>
              <CardContent>
                <GermanyMap 
                  selectedBundesland={project.bundesland}
                  height={180}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Angebot abgeben</DialogTitle>
            <DialogDescription>
              Beschreiben Sie Ihr Angebot für "{project.titel}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Preis (€) *</Label>
              <Input
                type="number"
                value={offerForm.preis}
                onChange={(e) => setOfferForm({ ...offerForm, preis: e.target.value })}
                placeholder="z.B. 5000"
                data-testid="offer-price-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Beschreibung *</Label>
              <Textarea
                value={offerForm.beschreibung}
                onChange={(e) => setOfferForm({ ...offerForm, beschreibung: e.target.value })}
                placeholder={t('placeholders.angebotBeschreiben')}
                rows={4}
                data-testid="offer-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zeitrahmen</Label>
                <Input
                  value={offerForm.zeitrahmen}
                  onChange={(e) => setOfferForm({ ...offerForm, zeitrahmen: e.target.value })}
                  placeholder="z.B. 2-3 Wochen"
                />
              </div>
              <div className="space-y-2">
                <Label>Verfügbar ab</Label>
                <Input
                  type="date"
                  value={offerForm.verfuegbar_ab}
                  onChange={(e) => setOfferForm({ ...offerForm, verfuegbar_ab: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowOfferDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmitOffer}
              disabled={submitting}
              className="bg-accent hover:bg-accent/90"
              data-testid="submit-offer-confirm-btn"
            >
              {submitting ? t('toasts.wirdGesendet') : t('toasts.angebotAbgeben')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default ProjectDetailPage;
